import { createSlice } from "@reduxjs/toolkit";
import { getEventList,getEventDetails,eventBooking,getCountryList } from "../actions/eventsActions";
import { getCookie,SetCookie } from "../../utils/utils";
const initialState={
    eventList:[],
    count:0,
    eventCollection:{},
    page:1,
    eventType:'All',
    eventDetails:'',
    presold:[],
    admissions:[],
    hasMore:false,
    // Loading flag for the event list specifically. Unlike the global
    // common.loading (toggled in the thunk BEFORE the fulfilled reducer runs),
    // this flips false in the SAME reducer that sets eventList — so the home
    // list never renders a frame where loading is done but events aren't in yet
    // (which briefly flashed the "No events" empty state).
    eventsLoading:false,
    // Dedicated loading flag for the event DETAILS page. Same rationale as
    // eventsLoading: it flips false in the SAME reducer that sets eventDetails,
    // so the page never renders a "loading done but data not in yet" frame. It
    // also frees the details gate from the shared, reference-counted
    // common.loading (which a concurrent getCountryList used to corrupt, blanking
    // the page on first load). Starts true so the very first paint — before the
    // fetch effect dispatches `pending` — shows the skeleton, not a NotFound flash.
    eventDetailsLoading:true,
    // requestId of the most recently started getEventList call. Used to ignore
    // stale responses when filters are clicked rapidly (take-latest semantics).
    listRequestId:null,
    getCountryFeeListDetails:getCookie("_a_bDetails")?JSON.parse(getCookie("_a_bDetails")):'',
    // In-flight guard for getCountryList. Many components (TicketBooking,
    // Payment, RegisterTickets, …) independently dispatch getCountryList on
    // mount; without this flag they all fire before the first response lands,
    // producing ~7 duplicate requests on the details page. The thunk's
    // `condition` checks this so only ONE request goes out.
    countryListLoading:false,
    selectTickets:[],
    country: getCookie("_global_country")?getCookie("_global_country"):'Jamaica'
}

const eventSlice=createSlice({
    name:'event',
    initialState:initialState,
    reducers:{
        eventList:(state,action)=>{
            state.eventList=action.payload;
        },
        setEventType:(state,action)=>{
            if(action.payload.eventType){
                state.eventType = action.payload.eventType;
            }
            if(action.payload.page){
                state.page = action.payload.page;
            }
        },
        setSelectedTicket:(state,action)=>{
            console.log("action selected ticket handle here",action);
            state.selectTickets=action.payload;
        },
        setCountry:(state,action)=>{
            SetCookie("_global_country", action.payload, 10);
            state.country=action.payload;
        },
        resetEventList:(state,action)=>{
            state.eventList = [];
        },
        resetPageAndHasMore: (state, action) => {
            state.eventList = [];
            state.page = 1;
            state.hasMore = true
        }
    },
    extraReducers:(builder)=>{
    builder.addCase(getEventList.pending,(state,action)=>{
        // Mark this as the latest in-flight request.
        state.listRequestId = action.meta.requestId;
        state.eventsLoading = true;
    }).addCase(getEventList.fulfilled,(state,action)=>{
        // Ignore stale responses: only the most recent request may update the
        // list. This prevents a slow earlier filter request from overwriting
        // the results of a newer one when the user clicks filters quickly.
        // (eventsLoading is left alone for stale responses — the latest request
        // is still in flight, so loading must stay true.)
        if(action.meta.requestId !== state.listRequestId){
            return;
        }
        if(action.payload.pagination.current_page != 1){
            // Append the next page, de-duplicated by id. With unstable pagination
            // (or a page refetched mid-scroll) the server can return an event that
            // was already on an earlier page; a plain concat would then render it
            // twice in the feed. Keep the first occurrence and drop repeats so
            // every event shows exactly once. Falls back to slug when id is absent.
            const keyOf = (e) => e?.id ?? e?.slug;
            const seen = new Set(state.eventList.map(keyOf));
            const fresh = (action.payload.result || []).filter((e) => {
                const k = keyOf(e);
                if (k == null || seen.has(k)) return false;
                seen.add(k);
                return true;
            });
            state.eventList = state.eventList.concat(fresh);
        }
        else{
            state.eventList = action.payload.result;
        }
        state.count=action.payload.pagination.last_page;
        state.page=action.payload.pagination.current_page;
        state.hasMore=action.payload.pagination.has_next_page;
        // Cleared together with the list update so the UI never sees a
        // loading-done-but-list-empty frame.
        state.eventsLoading = false;
    }).addCase(getEventList.rejected,(state,action)=>{
        // Only the latest request controls the flag. A superseded/aborted
        // request rejects with a stale requestId and must NOT stop the spinner,
        // because a newer request is still loading.
        if(action.meta.requestId === state.listRequestId){
            state.eventsLoading = false;
        }
    }).addCase(getEventDetails.pending,(state)=>{
        state.eventDetailsLoading = true;
    }).addCase(getEventDetails.fulfilled,(state,action)=>{
        state.eventDetails = action.payload;
        // Cleared together with the data set (here, not in the thunk) so the page
        // never sees a loading-done-but-data-not-in frame, and independent of the
        // shared common.loading counter.
        state.eventDetailsLoading = false;

        // Map the normalized event_tickets payload (one row per tier) into the
        // legacy `admissions` + `presold` shape that the downstream UI
        // (eventsDetails.jsx, TicketBooking2.jsx, etc.) already consumes.
        // Doing the translation here keeps every existing component working
        // without changes — the backend response is now the only source of
        // truth and the wide-column event_admission table is no longer read.
        //
        // For each ticket tier we emit:
        //   - a "Presold" entry when ticket.price > 0 (the pre-event price)
        //   - a "Gate" entry when ticket.door_price > 0 (the at-the-door price)
        // The same tier can produce both — that's how multi-price tickets
        // show up grouped under one name in the UI.
        const presold_tckts = [];
        const ad = [];

        const tickets = Array.isArray(action.payload?.tickets) ? action.payload.tickets : [];

        tickets.forEach((ticket) => {
            const currency = ticket.currency ? `${ticket.currency} ` : '$';
            const isUnlimited = !!ticket.is_unlimited;
            // Treat sold-out tickets as having qty 0 so the UI's "X left"
            // label can render "Sold Out" rather than a stale positive number.
            const qtyLeft = ticket.is_sold_out
                ? 0
                : (isUnlimited ? null : Number(ticket.quantity_left ?? 0));

            // Presold entry — the standard online tier. Included for every
            // priced tier AND every FREE tier, so free tickets show up in the
            // Select Tickets modal. A tier is "free" when it has no online price
            // AND no at-the-door price — detected by economics (price<=0 &&
            // door<=0), NOT by the ticket_type flag, since legacy/free tiers are
            // often stored as ticket_type 'paid' with price 0. Gate-only tiers
            // (price 0 but a positive door_price) are still skipped — they're
            // sold at the door, not claimable online. Complimentary tiers never
            // reach here (the backend excludes them from the details payload).
            const ticketPrice = Number(ticket.price);
            const ticketDoor  = Number(ticket.door_price);
            const isFreeTicket = ticket.ticket_type === 'free'
                || (ticketPrice <= 0 && ticketDoor <= 0);
            if (ticketPrice > 0 || isFreeTicket) {
                const presoldDetails = {
                    name: ticket.name,
                    fee: Number(ticket.price),
                    description: ticket.description,
                    currency,
                    type: 'Presold',
                    // Replaces the legacy `name_column` field. Downstream
                    // PaymentController identifies the tier by id, not by
                    // a hardcoded column name string.
                    event_ticket_id: ticket.id,
                    name_column: ticket.id,
                    qty: qtyLeft,
                    is_on_sale: !!ticket.is_on_sale,
                    is_sold_out: !!ticket.is_sold_out,
                    is_unlimited: isUnlimited,
                    sale_starts: ticket.sale_starts ?? null,
                    sale_ends: ticket.sale_ends ?? null,
                    pass_payment_fee_to_buyer: !!ticket.pass_payment_fee_to_buyer,
                    pass_service_fee_to_buyer: !!ticket.pass_service_fee_to_buyer,
                };
                presold_tckts.push(presoldDetails);
                ad.push(presoldDetails);
            }

            // Gate entry — the at-the-door price. Skipped when door_price is 0 or null.
            if (Number(ticket.door_price) > 0) {
                ad.push({
                    name: ticket.name,
                    fee: Number(ticket.door_price),
                    description: ticket.description,
                    currency,
                    type: 'Gate',
                    event_ticket_id: ticket.id,
                });
            }
        });

        state.presold = presold_tckts;
        state.admissions = Object.entries(
            ad.reduce((acc, obj) => {
                const { name, ...rest } = obj;
                if (!acc[name]) {
                    acc[name] = { name, data: [] };
                }
                acc[name].data.push(rest);
                return acc;
            }, {})
        ).map(([, value]) => value);
    }).addCase(getEventDetails.rejected,(state)=>{
        // A failed load resolves the page to NotFound. Use null (not the initial
        // '') so the details gate can tell "not found" apart from "loading".
        state.eventDetails = null;
        state.eventDetailsLoading = false;
    }).addCase(eventBooking.fulfilled,(state,actions)=>{
        console.log("event booking action dispatching here");
    }).addCase(getCountryList.pending,(state)=>{
        state.countryListLoading=true;
    }).addCase(getCountryList.fulfilled,(state,action)=>{
        let data=JSON.stringify(action.payload.result);
        SetCookie("_a_bDetails",data,10);
        state.getCountryFeeListDetails=JSON.parse(data);
        state.countryListLoading=false;
    }).addCase(getCountryList.rejected,(state)=>{
        state.countryListLoading=false;
    })
    }
});

export const {eventList,setEventType,setSelectedTicket,setCountry, resetEventList, resetPageAndHasMore} =eventSlice.actions;
export default eventSlice.reducer;
